import { apiClient } from '@/lib/api-client';
import type {
  CrmConta,
  CrmContato,
  CrmBoard,
  CrmBoardColumn,
  CrmNegocio,
  CrmNegocioMovimentacao,
  CrmAtividade,
  CrmNegocioComentario,
  CrmNegocioAnexo,
  CrmImportResultado,
  CrmApiKey,
  CrmApiKeyCriada,
} from '@/types/crm';

interface ApiResponse<T> {
  success: boolean;
  data: T;
  error?: string;
}

export const crmContasService = {
  getAll(empresaId?: string): Promise<ApiResponse<CrmConta[]>> {
    const query = empresaId ? `?empresa_id=${encodeURIComponent(empresaId)}` : '';
    return apiClient.get<ApiResponse<CrmConta[]>>(`/crm/contas${query}`);
  },
  getById(id: string): Promise<ApiResponse<CrmConta>> {
    return apiClient.get<ApiResponse<CrmConta>>(`/crm/contas/${id}`);
  },
  create(data: Partial<CrmConta>): Promise<ApiResponse<CrmConta>> {
    return apiClient.post<ApiResponse<CrmConta>>('/crm/contas', data);
  },
  update(id: string, data: Partial<CrmConta>): Promise<ApiResponse<CrmConta>> {
    return apiClient.put<ApiResponse<CrmConta>>(`/crm/contas/${id}`, data);
  },
  delete(id: string): Promise<ApiResponse<boolean>> {
    return apiClient.delete<ApiResponse<boolean>>(`/crm/contas/${id}`);
  },
  importCsv(file: File, params: { empresaId?: string }): Promise<ApiResponse<CrmImportResultado>> {
    const formData = new FormData();
    formData.append('file', file);
    if (params.empresaId) formData.append('empresa_id', params.empresaId);
    return apiClient.postMultipart<ApiResponse<CrmImportResultado>>('/crm/contas/import', formData);
  },
};

export const crmContatosService = {
  getAll(contaId?: string, empresaId?: string): Promise<ApiResponse<CrmContato[]>> {
    const params = new URLSearchParams();
    if (contaId) params.set('conta_id', contaId);
    if (empresaId) params.set('empresa_id', empresaId);
    const query = params.toString();
    return apiClient.get<ApiResponse<CrmContato[]>>(`/crm/contatos${query ? `?${query}` : ''}`);
  },
  getById(id: string): Promise<ApiResponse<CrmContato>> {
    return apiClient.get<ApiResponse<CrmContato>>(`/crm/contatos/${id}`);
  },
  create(data: Partial<CrmContato>): Promise<ApiResponse<CrmContato>> {
    return apiClient.post<ApiResponse<CrmContato>>('/crm/contatos', data);
  },
  update(id: string, data: Partial<CrmContato>): Promise<ApiResponse<CrmContato>> {
    return apiClient.put<ApiResponse<CrmContato>>(`/crm/contatos/${id}`, data);
  },
  delete(id: string): Promise<ApiResponse<boolean>> {
    return apiClient.delete<ApiResponse<boolean>>(`/crm/contatos/${id}`);
  },
  importCsv(file: File, params: { empresaId?: string }): Promise<ApiResponse<CrmImportResultado>> {
    const formData = new FormData();
    formData.append('file', file);
    if (params.empresaId) formData.append('empresa_id', params.empresaId);
    return apiClient.postMultipart<ApiResponse<CrmImportResultado>>('/crm/contatos/import', formData);
  },
};

export const crmBoardsService = {
  getAll(empresaId?: string): Promise<ApiResponse<CrmBoard[]>> {
    const query = empresaId ? `?empresa_id=${encodeURIComponent(empresaId)}` : '';
    return apiClient.get<ApiResponse<CrmBoard[]>>(`/crm/boards${query}`);
  },
  getById(id: string): Promise<ApiResponse<CrmBoard>> {
    return apiClient.get<ApiResponse<CrmBoard>>(`/crm/boards/${id}`);
  },
  create(data: {
    nome: string;
    empresa_id?: string;
    colunas?: Array<{ nome: string; cor: string; is_ganho?: boolean; is_perdido?: boolean }>;
  }): Promise<ApiResponse<CrmBoard>> {
    return apiClient.post<ApiResponse<CrmBoard>>('/crm/boards', data);
  },
  update(id: string, data: Partial<Pick<CrmBoard, 'nome' | 'is_default'>>): Promise<ApiResponse<CrmBoard>> {
    return apiClient.put<ApiResponse<CrmBoard>>(`/crm/boards/${id}`, data);
  },
  delete(id: string): Promise<ApiResponse<boolean>> {
    return apiClient.delete<ApiResponse<boolean>>(`/crm/boards/${id}`);
  },
  createColumn(
    boardId: string,
    data: { nome: string; cor?: string; is_ganho?: boolean; is_perdido?: boolean },
  ): Promise<ApiResponse<CrmBoardColumn>> {
    return apiClient.post<ApiResponse<CrmBoardColumn>>(`/crm/boards/${boardId}/colunas`, data);
  },
  updateColumn(
    columnId: string,
    data: Partial<Pick<CrmBoardColumn, 'nome' | 'cor' | 'is_ganho' | 'is_perdido'>>,
  ): Promise<ApiResponse<CrmBoardColumn>> {
    return apiClient.put<ApiResponse<CrmBoardColumn>>(`/crm/boards/colunas/${columnId}`, data);
  },
  deleteColumn(columnId: string): Promise<ApiResponse<boolean>> {
    return apiClient.delete<ApiResponse<boolean>>(`/crm/boards/colunas/${columnId}`);
  },
  reorderColumn(columnId: string, direction: 'up' | 'down'): Promise<ApiResponse<boolean>> {
    return apiClient.patch<ApiResponse<boolean>>(`/crm/boards/colunas/${columnId}/reorder`, { direction });
  },
};

export const crmNegociosService = {
  getAll(boardId?: string, empresaId?: string): Promise<ApiResponse<CrmNegocio[]>> {
    const query = new URLSearchParams();
    if (boardId) query.set('board_id', boardId);
    if (empresaId) query.set('empresa_id', empresaId);
    const qs = query.toString();
    return apiClient.get<ApiResponse<CrmNegocio[]>>(`/crm/negocios${qs ? `?${qs}` : ''}`);
  },
  getById(id: string): Promise<ApiResponse<CrmNegocio>> {
    return apiClient.get<ApiResponse<CrmNegocio>>(`/crm/negocios/${id}`);
  },
  getMovimentacoes(id: string): Promise<ApiResponse<CrmNegocioMovimentacao[]>> {
    return apiClient.get<ApiResponse<CrmNegocioMovimentacao[]>>(`/crm/negocios/${id}/movimentacoes`);
  },
  create(data: {
    titulo: string;
    board_id: string;
    column_id?: string;
    valor?: number | null;
    conta_id?: string | null;
    contato_id?: string | null;
    responsavel_ids?: string[];
    data_previsao_fechamento?: string | null;
    empresa_id?: string;
    tags?: string[];
  }): Promise<ApiResponse<CrmNegocio>> {
    return apiClient.post<ApiResponse<CrmNegocio>>('/crm/negocios', data);
  },
  update(
    id: string,
    data: Partial<Pick<CrmNegocio, 'titulo' | 'valor' | 'conta_id' | 'contato_id' | 'data_previsao_fechamento' | 'tags'>> & {
      responsavel_ids?: string[];
    },
  ): Promise<ApiResponse<CrmNegocio>> {
    return apiClient.put<ApiResponse<CrmNegocio>>(`/crm/negocios/${id}`, data);
  },
  mover(id: string, columnId: string): Promise<ApiResponse<CrmNegocio>> {
    return apiClient.patch<ApiResponse<CrmNegocio>>(`/crm/negocios/${id}/mover`, { column_id: columnId });
  },
  vincularCronograma(id: string, cronogramaId: string | null): Promise<ApiResponse<CrmNegocio>> {
    return apiClient.patch<ApiResponse<CrmNegocio>>(`/crm/negocios/${id}/cronograma`, { cronograma_id: cronogramaId });
  },
  vincularOkr(
    id: string,
    data: { okr_id?: string | null; key_result_id?: string | null },
  ): Promise<ApiResponse<CrmNegocio>> {
    return apiClient.patch<ApiResponse<CrmNegocio>>(`/crm/negocios/${id}/okr`, data);
  },
  delete(id: string): Promise<ApiResponse<boolean>> {
    return apiClient.delete<ApiResponse<boolean>>(`/crm/negocios/${id}`);
  },
  importCsv(file: File, params: { empresaId?: string; boardId: string }): Promise<ApiResponse<CrmImportResultado>> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('board_id', params.boardId);
    if (params.empresaId) formData.append('empresa_id', params.empresaId);
    return apiClient.postMultipart<ApiResponse<CrmImportResultado>>('/crm/negocios/import', formData);
  },
};

export const crmAtividadesService = {
  getAll(params?: { negocio_id?: string; contato_id?: string }): Promise<ApiResponse<CrmAtividade[]>> {
    const query = new URLSearchParams();
    if (params?.negocio_id) query.set('negocio_id', params.negocio_id);
    if (params?.contato_id) query.set('contato_id', params.contato_id);
    const qs = query.toString();
    return apiClient.get<ApiResponse<CrmAtividade[]>>(`/crm/atividades${qs ? `?${qs}` : ''}`);
  },
  create(data: {
    negocio_id?: string | null;
    contato_id?: string | null;
    tipo: CrmAtividade['tipo'];
    titulo: string;
    descricao?: string | null;
    data_prevista: string;
    responsavel_id?: string;
    empresa_id?: string;
  }): Promise<ApiResponse<CrmAtividade>> {
    return apiClient.post<ApiResponse<CrmAtividade>>('/crm/atividades', data);
  },
  update(
    id: string,
    data: Partial<
      Pick<CrmAtividade, 'tipo' | 'titulo' | 'descricao' | 'data_prevista' | 'responsavel_id' | 'concluida' | 'calendario_evento_id'>
    >,
  ): Promise<ApiResponse<CrmAtividade>> {
    return apiClient.put<ApiResponse<CrmAtividade>>(`/crm/atividades/${id}`, data);
  },
  delete(id: string): Promise<ApiResponse<boolean>> {
    return apiClient.delete<ApiResponse<boolean>>(`/crm/atividades/${id}`);
  },
};

export const crmComentarioService = {
  getAll(negocioId: string): Promise<ApiResponse<CrmNegocioComentario[]>> {
    return apiClient.get<ApiResponse<CrmNegocioComentario[]>>(`/crm/negocios/${negocioId}/comentarios`);
  },
  create(negocioId: string, texto: string): Promise<ApiResponse<CrmNegocioComentario>> {
    return apiClient.post<ApiResponse<CrmNegocioComentario>>(`/crm/negocios/${negocioId}/comentarios`, { texto });
  },
  delete(negocioId: string, comentarioId: string): Promise<ApiResponse<boolean>> {
    return apiClient.delete<ApiResponse<boolean>>(`/crm/negocios/${negocioId}/comentarios/${comentarioId}`);
  },
};

export const crmAnexoService = {
  async list(negocioId: string): Promise<ApiResponse<CrmNegocioAnexo[]>> {
    try {
      return await apiClient.get<ApiResponse<CrmNegocioAnexo[]>>(`/crm/negocios/${negocioId}/anexos`);
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao buscar anexos', data: [] };
    }
  },
  async upload(negocioId: string, file: File): Promise<ApiResponse<CrmNegocioAnexo>> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      return await apiClient.postMultipart<ApiResponse<CrmNegocioAnexo>>(`/crm/negocios/${negocioId}/anexos`, formData);
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao enviar anexo', data: {} as CrmNegocioAnexo };
    }
  },
  async getConteudoBlob(negocioId: string, anexoId: string): Promise<Blob> {
    return apiClient.getBlob(`/crm/negocios/${negocioId}/anexos/${anexoId}/conteudo`);
  },
  async delete(negocioId: string, anexoId: string): Promise<ApiResponse<boolean>> {
    try {
      return await apiClient.delete<ApiResponse<boolean>>(`/crm/negocios/${negocioId}/anexos/${anexoId}`);
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao excluir anexo', data: false };
    }
  },
};

export const crmApiKeysService = {
  getAll(empresaId?: string): Promise<ApiResponse<CrmApiKey[]>> {
    const query = empresaId ? `?empresa_id=${encodeURIComponent(empresaId)}` : '';
    return apiClient.get<ApiResponse<CrmApiKey[]>>(`/crm/api-keys${query}`);
  },
  create(data: { nome: string; expira_em?: string | null; empresa_id?: string }): Promise<ApiResponse<CrmApiKeyCriada>> {
    return apiClient.post<ApiResponse<CrmApiKeyCriada>>('/crm/api-keys', data);
  },
  delete(id: string, empresaId?: string): Promise<ApiResponse<{ id: string }>> {
    const query = empresaId ? `?empresa_id=${encodeURIComponent(empresaId)}` : '';
    return apiClient.delete<ApiResponse<{ id: string }>>(`/crm/api-keys/${id}${query}`);
  },
};
